import React from 'react'
import tw from 'tailwind-styled-components'

interface SimpleMenuItemProps {
  label: string
  onClick?: () => void
  icon?: React.ReactNode
  iconSize?: number | string
  count?: number | string
  selected?: boolean
  marginBottom?: string
  textColor?: string
  textSize?: '12' | '14'
  size?: '28' | '32' | '36' | '40'
  highlightNumber?: boolean
  disabled?: boolean
}

const Container = tw.div<{
  $marginBottom?: string
  $selected?: boolean
  $size: '28' | '32' | '36' | '40'
}>`
  group
  flex
  flex-row
  items-center
  gap-4
  px-12
  py-2
  select-none

  ${({$size}) => {
    if ($size === '28') {
      return 'h-28'
    }
    if ($size === '32') {
      return 'h-32'
    }
    if ($size === '36') {
      return 'h-36'
    }
    if ($size === '40') {
      return 'h-40'
    }
    return 'h-32'
  }}

  ${({$selected}) =>
    $selected
      ? 'bg-interactive-l80'
      : 'hover:bg-interactive-l100 active:bg-interactive-l80'}
  ${({$marginBottom}) => $marginBottom || ''}
`

const Text = tw.div<{
  $textSize: '12' | '14'
  $textColor?: string
  $disabled?: boolean
}>`
  whitespace-nowrap
  w-full
  text-ellipsis
  overflow-hidden
  ${({$textColor, $disabled}) =>
    $textColor || ($disabled ? 'text-placeholder' : 'text-primary')}
  ${({$textSize}) =>
    $textSize === '12' ? 'font-normal text-12 leading-4' : 'plain-text-regular'}
`

const NumberDisplay = tw.div<{
  $highlightNumber?: boolean
}>`
  ml-auto
  text-11
  leading-4
  whitespace-nowrap
  ${({$highlightNumber}) =>
    $highlightNumber ? 'text-error-l20' : 'text-placeholder'}
`

const IconWrapper = tw.div`
  flex
  items-center
`

/**
 * Simple menu item
 * @unstable Only base functionality was tested
 * @param label
 * @param onClick
 * @param icon
 * @param iconSize
 * @param count
 * @param selected
 * @param marginBottom
 * @param textColor
 * @param textSize
 * @param size
 * @param highlightNumber
 * @constructor
 */
export const SimpleMenuItem: React.FC<SimpleMenuItemProps> = ({
  label,
  onClick,
  icon,
  iconSize,
  count,
  selected,
  marginBottom,
  textColor,
  textSize = '14',
  size = '28',
  highlightNumber,
  disabled
}) => {
  const iconStyle = iconSize ? {width: iconSize, height: iconSize} : {}

  return (
    <Container
      $selected={selected}
      onClick={!disabled ? onClick : undefined}
      $marginBottom={marginBottom}
      $size={size}
    >
      {icon && <IconWrapper style={iconStyle}>{icon}</IconWrapper>}
      <Text $textSize={textSize} $textColor={textColor} $disabled={disabled}>
        {label}
      </Text>
      {count !== undefined && (
        <NumberDisplay $highlightNumber={highlightNumber}>
          {count}
        </NumberDisplay>
      )}
    </Container>
  )
}
